import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";

import {
  ApiEnvelope,
  PatientPaymentByVisit,
} from "@/pages/app/provider/billings/billing-types";
import { PatientPaymentControllerApi } from "../../../services";
import { getConfigObject, getXtenantId } from "../../../utils/utils";

/**
 * Consolidated patient-payment summary for a single visit (ECRH-5039).
 *
 * Endpoint: GET /api/master/patient-payment/v2/by-visit
 * The response (`data`) carries:
 *  - advancePayment  -> pre-collected payment for THIS visit (Adv Paid)
 *  - previousBalance -> outstanding balance across ALL prior visits (Prev. Balance)
 *
 * Used to auto-populate the Patient Balance section of the
 * "Create Invoice for Patient Responsibility" flow.
 *
 * @author Atish Bagate
 */
interface Props {
  patientUuid?: string;
  visitId?: string;
  enabled?: boolean;
}

export function useGetPatientPaymentsByVisit({
  patientUuid,
  visitId,
  enabled = true,
}: Props): UseQueryResult<
  ApiEnvelope<PatientPaymentByVisit> | undefined,
  AxiosError
> {
  return useQuery({
    queryKey: ["get-patient-payments-by-visit", patientUuid, visitId],
    refetchOnWindowFocus: false,
    enabled: enabled && !!patientUuid && !!visitId,
    queryFn: async () =>
      new PatientPaymentControllerApi(getConfigObject())
        .getPatientPaymentsByVisit(
          patientUuid ?? "",
          visitId ?? "",
          getXtenantId()
        )
        .then((it) => it?.data as ApiEnvelope<PatientPaymentByVisit>),
  });
}
